// TransferClients.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Grid,
  CircularProgress,
  TextField,
  Alert,
} from "@mui/material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useTranslation } from "react-i18next";

const TransferClients = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [activeStep, setActiveStep] = useState(0);

  const [sourceUserId, setSourceUserId] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientsData, setClientsData] = useState(null);
  const [targetUserId, setTargetUserId] = useState("");

  // States for Range Selection
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [rangeError, setRangeError] = useState("");
  const [rangeSelectedClients, setRangeSelectedClients] = useState([]);

  const steps = t("call_center.steps", { returnObjects: true });

  // Fetch Users
  const {
    data: users,
    isLoading: usersLoading,
    isError: usersError,
  } = useFetchObjects(["users"], `users/?call_center=true`, token);

  // Fetch Clients based on Source User without pagination
  const {
    data: clients,
    isLoading: clientsLoading,
    isError: clientsError,
    refetch: refetchClients,
  } = useFetchObjects(
    ["clients", sourceUserId],
    sourceUserId ? `all-clients/?follow_up_by=${sourceUserId}` : null, // Removed page_size
    token
  );

  // Update Clients Data when fetched
  useEffect(() => {
    if (clients && sourceUserId) {
      setClientsData(clients);
    }
  }, [clients, sourceUserId]);

  // Handle Transfer Action
  const {
    handleAdd,
    loading: transferLoading,
    resetForm,
  } = useAdd(
    "transfer-clients",
    token,
    t("call_center.clientsSuccessTransfer"),
    t("call_center.clientsErrorTransfer")
  );

  // Reset Form after Successful Transfer
  useEffect(() => {
    if (resetForm) {
      setActiveStep(0);
      setSourceUserId("");
      setSelectedClients([]);
      setTargetUserId("");
      setClientsData(null);
      setRangeFrom("");
      setRangeTo("");
      setRangeError("");
      setRangeSelectedClients([]);
    }
  }, [resetForm]);

  // Handle Next Button Click
  const handleNext = () => {
    if (activeStep === 0 && sourceUserId) {
      refetchClients();
    }

    if (activeStep === 1) {
      if (selectedClients.length === 0 && rangeSelectedClients.length === 0) {
        alert(t("call_center.selectAtLeastOneClient"));
        return;
      }
      if (rangeError) {
        alert(t("call_center.fixRangeErrors"));
        return;
      }
    }

    if (activeStep === 2 && !targetUserId) {
      alert(t("call_center.selectATargetUser"));
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle Back Button Click
  const handleBack = () => {
    if (activeStep === 1) {
      setSelectedClients([]);
      setRangeFrom("");
      setRangeTo("");
      setRangeError("");
      setRangeSelectedClients([]);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle Transfer Action
  const handleTransfer = () => {
    // Combine selectedClients and rangeSelectedClients, remove duplicates
    const combinedClients = Array.from(
      new Set([...selectedClients, ...rangeSelectedClients])
    );

    if (combinedClients.length === 0) {
      alert(t("call_center.noClientsToTransfer"));
      return;
    }

    const payload = {
      follow_up_by: sourceUserId,
      clients: combinedClients,
      transfer_to: targetUserId,
    };

    handleAdd(payload);
  };

  // Select All Clients
  const isAllSelected =
    clientsData && selectedClients.length === clientsData.length;
  const isIndeterminate =
    clientsData &&
    selectedClients.length > 0 &&
    selectedClients.length < clientsData.length;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allClientIds = clientsData.map((client) => client.id);
      setSelectedClients(allClientIds);
    } else {
      setSelectedClients([]);
    }
  };

  // Handle Client Selection Toggle
  const handleClientToggle = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter((id) => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  // Validate and Set Range Selected Clients
  useEffect(() => {
    // Validate range
    if (rangeFrom && rangeTo) {
      const from = parseInt(rangeFrom, 10);
      const to = parseInt(rangeTo, 10);

      if (isNaN(from) || isNaN(to)) {
        setRangeError(t("call_center.enterValidNumbers"));
        setRangeSelectedClients([]);
        return;
      }

      if (from > to) {
        setRangeError(t("call_center.invalidRange"));
        setRangeSelectedClients([]);
        return;
      }

      // Filter clients within range
      if (clientsData) {
        const rangedClients = clientsData
          .filter((client) => client.id >= from && client.id <= to)
          .map((client) => client.id);

        if (rangedClients.length === 0) {
          setRangeError(t("call_center.noClientsInRange"));
        } else {
          setRangeError("");
        }

        setRangeSelectedClients(rangedClients);
      }
    } else {
      setRangeError("");
      setRangeSelectedClients([]);
    }
  }, [rangeFrom, rangeTo, clientsData, t]);

  // Render Content Based on Active Step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" mb={2}>
              {t("call_center.selectUserFrom")}
            </Typography>
            {usersLoading && <CircularProgress />}
            {usersError && (
              <Typography color="error">
                {t("call_center.failedToLoadUsers")}
              </Typography>
            )}
            {!usersLoading && !usersError && (
              <FormControl fullWidth>
                <Select
                  value={sourceUserId}
                  onChange={(e) => {
                    setSourceUserId(e.target.value);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t("call_center.selectUserFrom")}</em>
                  </MenuItem>
                  {users?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" mb={2}>
              {t("call_center.selectClientsToTransfer")}
            </Typography>
            {clientsLoading && <CircularProgress />}
            {clientsError && (
              <Typography color="error">
                {t("call_center.failedToLoadClients")}
              </Typography>
            )}
            {!clientsLoading &&
              !clientsError &&
              clientsData &&
              clientsData.length === 0 && (
                <Typography>{t("call_center.noClientsFound")}</Typography>
              )}
            {!clientsLoading &&
              !clientsError &&
              clientsData &&
              clientsData.length > 0 && (
                <Box>
                  {/* Range Selection */}
                  <Paper sx={{ mb: 3, p: 2 }}>
                    <Typography variant="subtitle1" mb={1}>
                      {t("call_center.rangeSelection")}
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <TextField
                          type="number"
                          label={t("call_center.fromID")}
                          value={rangeFrom}
                          onChange={(e) => setRangeFrom(e.target.value)}
                          fullWidth
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          type="number"
                          label={t("call_center.toID")}
                          value={rangeTo}
                          onChange={(e) => setRangeTo(e.target.value)}
                          fullWidth
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>
                    </Grid>
                    {rangeError && (
                      <Box mt={2}>
                        <Alert severity="error">{rangeError}</Alert>
                      </Box>
                    )}
                    {rangeSelectedClients.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" mb={1}>
                          {t("call_center.clientsInRange")}:
                        </Typography>
                        <List dense>
                          {clientsData
                            .filter(
                              (client) =>
                                client.id >= parseInt(rangeFrom, 10) &&
                                client.id <= parseInt(rangeTo, 10)
                            )
                            .map((client) => (
                              <ListItem key={client.id}>
                                <ListItemText
                                  primary={`${client.id} - ${client.full_name}`}
                                  secondary={`Contact: ${
                                    client.contact || "N/A"
                                  }, Procedure: ${
                                    client?.display_procedure?.name || "N/A"
                                  }`}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    )}
                  </Paper>

                  {/* Manual Selection */}
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" mb={1}>
                      {t("call_center.manualSelection")}
                    </Typography>
                    <List dense>
                      {/* Select All Option */}
                      <ListItem>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            inputProps={{ "aria-label": "select all clients" }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("call_center.selectAll")}
                          onClick={() => {
                            handleSelectAll({
                              target: { checked: !isAllSelected },
                            });
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </ListItem>
                      {/* Divider */}
                      <Box sx={{ borderBottom: "1px solid #e0e0e0" }} />
                      {/* Client Items */}
                      {clientsData.map((client) => {
                        const labelId = `checkbox-list-label-${client.id}`;
                        return (
                          <ListItem
                            key={client.id}
                            button
                            onClick={() => handleClientToggle(client.id)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selectedClients.includes(client.id)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              id={labelId}
                              primary={`${client.id} - ${client.full_name}`}
                              secondary={`Contact: ${
                                client.contact || "N/A"
                              }, Procedure: ${
                                client?.display_procedure?.name || "N/A"
                              }`}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                </Box>
              )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" mb={2}>
              {t("call_center.selectTargetUser")}
            </Typography>
            {usersLoading && <CircularProgress />}
            {usersError && (
              <Typography color="error">
                {t("call_center.failedToLoadUsers")}
              </Typography>
            )}
            {!usersLoading && !usersError && (
              <FormControl fullWidth>
                <Select
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t("call_center.selectTargetUser")}</em>
                  </MenuItem>
                  {users?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Grid container justifyContent="center" mt={5}>
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" mb={4}>
            {t("call_center.title")}
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box mt={4}>
            {activeStep === steps.length ? (
              <Box textAlign="center">
                <Typography variant="h6">
                  {t("call_center.allStepsCompleted")}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setActiveStep(0)}
                  sx={{ mt: 2 }}
                >
                  {t("call_center.reset")}
                </Button>
              </Box>
            ) : (
              <Box>
                {renderStepContent(activeStep)}
                <Box mt={4} display="flex" justifyContent="space-between">
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                  >
                    {t("call_center.back")}
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTransfer}
                      disabled={transferLoading}
                    >
                      {transferLoading
                        ? t("call_center.transferring")
                        : t("call_center.transfer")}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      disabled={
                        (activeStep === 0 && !sourceUserId) ||
                        (activeStep === 1 &&
                          (clientsLoading ||
                            clientsError ||
                            (rangeFrom && rangeTo && rangeError)))
                      }
                    >
                      {t("call_center.next")}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TransferClients;
